import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { PageContainer, Tile } from 'components/Layout/PageContainer';
import { Box, Typography } from '@mui/material';

import { useAnalysisData, useMessage } from 'pages/trading/queryHooks';
import { PriceChange } from 'pages/trading/model';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { StyleDetailChart } from '../StyleDetailChart';
import { TradingMessageDisplay } from '../../TradingMessageDisplay';

import { transformData } from 'pages/trading/price/promotion/createPageDataStructures';
import { PromotionKpiMatrix } from 'pages/trading/price/promotion/PromotionKpiMatrix';
import { PromotionTable } from 'pages/trading/price/promotion/PromotionTable';

export function PromoStyleDetailPage() {
  const location = useLocation();
  const { messageId, styleId } = useParams<{ messageId: string; styleId: string }>();
  const setDetailPageBreadcrumb = useBreadcrumbStore(state => state.setDetailPageBreadcrumb);

  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);

  const { data: message } = useMessage(messageId);
  const { data: analysisData, isPending } = useAnalysisData(message?.analysisDataId);

  const styleData = useMemo(() => {
    if (!analysisData?.data) return [];
    const decodedTarget = decodeURIComponent(styleId ?? '');
    const [targetUniqueId, targetChannel] = decodedTarget.split('::');
    return (analysisData.data as PriceChange[]).filter(row => {
      const rowFullId = row.styNumber || row.prdNumber;
      if (targetChannel) {
        return rowFullId === targetUniqueId && (row.chnName || '') === targetChannel;
      }
      return rowFullId === targetUniqueId;
    });
  }, [analysisData, styleId]);

  const navState = location.state as { displayTitle?: string } | undefined;
  const displayTitle = useMemo(() => {
    if (navState?.displayTitle) return navState.displayTitle;
    const styleInfo = styleData[0];
    if (!styleInfo) return styleId ?? '';
    const name = styleInfo.prdName || styleId || '';
    const color = styleInfo.color || (styleInfo.styNumber?.includes('|') ? styleInfo.styNumber.split('|')[1] : '');
    return `${name}${color ? ` | ${color}` : ''}`;
  }, [navState, styleData, styleId]);

  useEffect(() => {
    if (message) {
      setDetailPageBreadcrumb(location.pathname, [
        { attribute: 'messageId', values: [messageId ?? ''] },
        { attribute: 'styleId', values: [displayTitle ?? ''] },
      ]);
    }
  }, [message, messageId, displayTitle, location.pathname, setDetailPageBreadcrumb]);

  const kpis = useMemo(() => {
    return transformData(styleData);
  }, [styleData]);

  const filteredData = useMemo(() => {
    return selectedArticleId ? styleData.filter(d => d.prdNumber === selectedArticleId) : styleData;
  }, [styleData, selectedArticleId]);

  if (isPending)
    return (
      <Box className="flex justify-center py-20">
        <LoadingSpinner />
      </Box>
    );
  if (!styleData.length) return <div className="p-10 text-center">Style data not found.</div>;

  return (
    <div className="standard-page-container">
      <PageContainer className="pb-8">
        <Tile className="pt-5">
          <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
            {displayTitle}
          </Typography>
        </Tile>

        {message && (
          <Tile className=" p-0 overflow-hidden">
            <TradingMessageDisplay message={message} />
          </Tile>
        )}

        <PromotionKpiMatrix matrix={kpis.matrix} />

        <Tile className="my-4">
          <StyleDetailChart
            data={filteredData}
            selectedArticleId={selectedArticleId}
            onClearFilter={() => setSelectedArticleId(null)}
            isPromotion={true}
          />
        </Tile>

        <Tile className="p-0 mt-4">
          <PromotionTable data={styleData} />
        </Tile>
      </PageContainer>
    </div>
  );
}
